'use client'
import React from 'react';
import { motion } from 'motion/react';
import { Heart, DollarSign, Factory, ShoppingBag, GraduationCap, Truck, ArrowRight } from 'lucide-react';
import { TCompaniesConfig } from '@/app/config';

const TransformingIndustries= ({config}:{config:TCompaniesConfig["TransformingIndustries"]}) => {
  const industriesIcon = [
    {
      icon: Heart,
     
    },
    {
      icon: DollarSign,
      
    },
    {
      icon: Factory,
     
    },
    {
      icon: ShoppingBag,
     
    },
    {
      icon: GraduationCap,
     
    },
    {
      icon: Truck,
     
    }
  ];
  const industriesConfig = config?.industries;
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3
      }
    }
  };



  const cardHoverVariants = {
    hover: {
      y: -8,
      scale: 1.03,
      rotateY: 2,
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.15)",
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  

  const backgroundVariants = {
    hover: {
      background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
      transition: {
        duration: 0.3
      }
    }
  };

  return (
    <motion.section 
      className="py-20 bg-gray-50"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={containerVariants}
    >
      <div className="max-w-7xl mx-auto px-8">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          variants={{
            hidden: { opacity: 0, y: 50 },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.8,
                ease: "easeOut"
              }
            }
          }}
        >
          <motion.h2 
            className="text-4xl font-bold text-gray-900 mb-4"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
          {config.title}
          </motion.h2>
          <motion.p 
            className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
          >
          {config.description}
          </motion.p>
        </motion.div>

        {/* Industries Grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={containerVariants}
        >
          {industriesConfig.map((industry, index) => {
            const IconComponent = industriesIcon[index].icon;
            return (
              <motion.div 
                key={index} 
                className="bg-white rounded-2xl p-6 shadow-sm cursor-pointer relative overflow-hidden"
                variants={{
                  hidden: { 
                    opacity: 0, 
                    y: 60,
                    scale: 0.8,
                    rotateX: -15
                  },
                  visible: {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    rotateX: 0,
                    transition: {
                      duration: 0.6,
                      ease: "easeOut"
                    }
                  }
                }}
                whileHover="hover"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                {...cardHoverVariants}
                style={{ perspective: "1000px" }}
              >
                {/* Animated background overlay */}
                <motion.div
                  className="absolute inset-0 opacity-0"
                  variants={backgroundVariants}
                  whileHover="hover"
                  style={{ zIndex: -1 }}
                />

                {/* Header with icon and title */}
                <motion.div 
                  className="flex items-center space-x-3 mb-4"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                >
                  <motion.div 
                    className={`w-10 h-10 ${industry.bgColor} rounded-xl flex items-center justify-center`}
                    variants={{
                      hidden: { 
                        scale: 0,
                        rotate: -180,
                        opacity: 0
                      },
                      visible: {
                        scale: 1,
                        rotate: 0,
                        opacity: 1,
                        transition: {
                          type: "spring",
                          stiffness: 200,
                          damping: 15,
                          delay: 0.2
                        }
                      },
                      hover: {
                        scale: 1.1,
                        rotate: [0, -5, 5, 0],
                        transition: {
                          duration: 0.4,
                          ease: "easeInOut"
                        }
                      }
                    }}
                    whileHover="hover"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <motion.div variants={{
                            hover: {
                              scale: 1.2,
                              transition: {
                                duration: 0.2,
                                ease: "easeInOut"
                              }
                            }
                          }} whileHover="hover">
                      <IconComponent className="w-5 h-5 text-white" />
                    </motion.div>
                  </motion.div>
                  
                  <div>
                    <motion.h3 
                      className="text-xl font-bold text-gray-900"
                      variants={ {
                        hidden: { opacity: 0, x: -20 },
                        visible: {
                          opacity: 1,
                          x: 0,
                          transition: {
                            duration: 0.5,
                            ease: "easeOut",
                            delay: 0.3
                          }
                        },
                        hover: {
                          color: "#16a34a",
                          transition: {
                            duration: 0.2
                          }
                        }
                      }}
                      whileHover="hover"
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 + 0.1 }}
                    >
                      {industry.title}
                    </motion.h3>
                    <motion.p 
                      className="text-sm text-green-600 font-medium"
                      variants={{
                        hidden: { opacity: 0, x: -20 },
                        visible: {
                          opacity: 1,
                          x: 0,
                          transition: {
                            duration: 0.5,
                            ease: "easeOut",
                            delay: 0.4
                          }
                        },
                        hover: {
                          scale: 1.05,
                          transition: {
                            duration: 0.2
                          }
                        }
                      }}
                      whileHover="hover"
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 + 0.2 }}
                    >
                      {industry.subtitle}
                    </motion.p>
                  </div>
                </motion.div>

                {/* Features list */}
                <motion.div 
                  className="space-y-3"
                  variants={containerVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                >
                  {industry.features.map((feature, featureIndex) => (
                    <motion.div 
                      key={featureIndex} 
                      className="flex items-center space-x-2"
                      variants={ {
                        hidden: { opacity: 0, x: -30 },
                        visible: {
                          opacity: 1,
                          x: 0,
                          transition: {
                            duration: 0.4,
                            ease: "easeOut"
                          }
                        },
                        hover: {
                          x: 8,
                          transition: {
                            duration: 0.2,
                            ease: "easeOut"
                          }
                        }
                      }}
                      whileHover="hover"
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 + featureIndex * 0.1 + 0.5 }}
                    >
                      <motion.div
                        variants={ {
                          hidden: { scale: 0, rotate: -90 },
                          visible: {
                            scale: 1,
                            rotate: 0,
                            transition: {
                              type: "spring",
                              stiffness: 300,
                              damping: 20
                            }
                          },
                          hover: {
                            x: 5,
                            scale: 1.2,
                            rotate: 45,
                            color: "#16a34a",
                            transition: {
                              duration: 0.2,
                              ease: "easeOut"
                            }
                          }
                        }}
                        whileHover="hover"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 + featureIndex * 0.1 + 0.6 }}
                      >
                        <ArrowRight className="w-4 h-4 text-green-500" />
                      </motion.div>
                      <motion.span 
                        className="text-sm text-gray-600"
                        whileHover={{ 
                          color: "#16a34a",
                          fontWeight: 500
                        }}
                        transition={{ duration: 0.2 }}
                      >
                        {feature}
                      </motion.span>
                    </motion.div>
                  ))}
                </motion.div>

                {/* Shimmer effect on hover */}
                <motion.div
                  className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-white to-transparent opacity-0"
                  initial={{ x: "-100%" }}
                  whileHover={{
                    x: "100%",
                    opacity: 0.1,
                  }}
                  transition={{
                    duration: 0.6,
                    ease: "easeInOut"
                  }}
                  style={{
                    background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)"
                  }}
                />
              </motion.div>
            );
          })}
        </motion.div>

        {/* Floating decorative elements */}
        <motion.div 
          className="absolute inset-0 overflow-hidden pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.5 }}
        >
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className={`absolute w-2 h-2 rounded-full ${
                i % 3 === 0 ? 'bg-orange-300' : 
                i % 3 === 1 ? 'bg-yellow-300' : 'bg-green-300'
              }`}
              style={{
                left: `${10 + (i * 15) % 80}%`,
                top: `${15 + (i * 12) % 70}%`,
              }}
              animate={{
                y: [-20, 20, -20],
                x: [-10, 10, -10],
                opacity: [0.3, 0.7, 0.3],
                scale: [0.8, 1.2, 0.8],
                rotate: [0, 180, 360],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                delay: i * 0.7,
                ease: "easeInOut"
              }}
            />
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
};

export default TransformingIndustries;